import { hotPreview, categories } from "@/mocks/chaqua";

interface HeroProps {
  activeCat: string;
  onPickCat: (key: string) => void;
}

const TAG_STYLE: Record<string, string> = {
  爆: "bg-coral-500 text-white",
  热: "bg-coral-100 text-coral-600",
  新: "bg-leaf-100 text-leaf-600",
};

export default function Hero({ activeCat, onPickCat }: HeroProps) {
  return (
    <section
      className="relative overflow-hidden pt-16"
      aria-label="今日热点首屏"
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-coral-100/60 blur-3xl"></div>
        <div className="absolute -right-20 top-40 h-80 w-80 rounded-full bg-leaf-100/50 blur-3xl"></div>
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-cream-50"></div>
      </div>

      <div className="mx-auto max-w-6xl px-4 pb-12 pt-12 md:px-6 md:pt-16">
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-coral-200 bg-white/70 px-3 py-1.5 text-xs font-semibold text-coral-600">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-coral-400 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-coral-500"></span>
              </span>
              今日瓜点已更新 · 09月07日
            </span>

            <h1 className="mt-5 text-3xl font-black leading-tight tracking-tight text-stone-900 md:text-5xl">
              今天全网都在聊什么？
              <br />
              <span className="text-coral-500">吃瓜在线</span>，新鲜事一眼看全
            </h1>

            <p className="mt-5 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg">
              实时汇总<span className="font-semibold text-stone-800">今日大瓜、今日热议与今日新鲜事</span>，
              明星动态、影视娱乐、音乐现场、体坛竞技、社会趣闻一网打尽。打开吃瓜在线，不再错过任何一个值得讨论的话题。
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-stone-500">热门栏目：</span>
              {categories
                .filter((c) => c.key !== "all")
                .slice(0, 5)
                .map((c) => (
                  <a
                    key={c.key}
                    href="#feed"
                    onClick={() => onPickCat(c.key)}
                    className={`flex items-center gap-1 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                      activeCat === c.key
                        ? "border-coral-500 bg-coral-500 text-white"
                        : "border-cream-300 bg-white text-stone-600 hover:border-coral-300 hover:text-coral-600"
                    }`}
                  >
                    <i className={c.icon} aria-hidden="true"></i>
                    {c.name}
                  </a>
                ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#hot"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-coral-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-coral-600"
              >
                <i className="ri-fire-line mr-1.5" aria-hidden="true"></i>去看今日热榜
              </a>
              <a
                href="#big"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-stone-300 bg-white px-6 py-3 text-sm font-semibold text-stone-700 transition hover:border-coral-300 hover:text-coral-600"
              >
                <i className="ri-download-cloud-line mr-1.5" aria-hidden="true"></i>开刷今日大瓜
              </a>
            </div>
          </div>

          <div className="rounded-3xl border border-cream-200 bg-white/80 p-5 shadow-sm backdrop-blur-sm md:p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-base font-bold text-stone-900">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-coral-50 text-coral-500">
                  <i className="ri-bubble-chart-line" aria-hidden="true"></i>
                </span>
                此刻全网最热
              </h2>
              <a href="#hot" className="text-xs font-medium text-coral-500 hover:underline whitespace-nowrap">
                完整热榜 <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
              </a>
            </div>
            <ol className="flex flex-col gap-3">
              {hotPreview.map((item, idx) => (
                <li key={item.id} className="flex items-start gap-3">
                  <span className="font-display-num w-6 shrink-0 pt-0.5 text-right text-xl font-bold text-coral-400">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                      <span
                        className={`mt-0.5 inline-flex h-5 shrink-0 items-center rounded px-1.5 text-[11px] font-bold leading-5 ${TAG_STYLE[item.tag]}`}
                      >
                        {item.tag}
                      </span>
                      <p className="text-sm font-semibold leading-snug text-stone-800">{item.title}</p>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 pl-1 text-xs text-stone-400">
                      <span className="font-display-num">{item.heatText} 热度</span>
                      <span className="h-1 flex-1 rounded-full bg-cream-100">
                        <span className="block h-1 rounded-full bg-coral-400" style={{ width: `${item.heatNum}%` }}></span>
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}